import React, { useState, useEffect, useCallback } from 'react';
import { storageService, SavedIdea, IdeaFolder, HistoryItem } from '../services/storageService';
import { useT, formatRelativeTime } from '../lib/i18n';
import { cn } from '../lib/cn';

const typeStyles: Record<string, string> = {
  tweet: 'bg-brand-muted text-brand',
  analysis: 'bg-accent-muted text-accent',
  coaching: 'bg-warning-muted text-warning',
  reply: 'bg-success-muted text-success',
};

export const RightSidebar: React.FC = () => {
  const { t, locale } = useT();
  const [activeTab, setActiveTab] = useState<'ideas' | 'history'>('ideas');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [ideas, setIdeas] = useState<SavedIdea[]>([]);
  const [folders, setFolders] = useState<IdeaFolder[]>([]);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const reload = useCallback(() => {
    setIdeas(storageService.getSavedIdeas());
    setFolders(storageService.getFolders());
    setHistoryItems(storageService.getHistoryItems());
  }, []);

  useEffect(() => {
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'bangerfy_saved_ideas' || e.key === 'bangerfy_folders' || e.key === 'bangerfy_history') {
        reload();
      }
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(reload, 1000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [reload]);

  const filteredIdeas = selectedFolder === 'all'
    ? ideas
    : ideas.filter(idea => idea.folder === selectedFolder);

  const getFolderCount = (folderId: string) => {
    if (folderId === 'all') return ideas.length;
    return ideas.filter(i => i.folder === folderId).length;
  };

  const handleAddFolder = () => {
    if (!newFolderName.trim()) return;
    storageService.addFolder(newFolderName.trim());
    setNewFolderName('');
    setShowNewFolder(false);
    reload();
  };

  const handleDeleteIdea = (id: string) => {
    storageService.removeSavedIdea(id);
    reload();
  };

  return (
    <aside className="w-full h-full bg-surface-1 border-l border-border flex flex-col">
      {/* Tabs */}
      <div className="flex">
        {(['ideas', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 py-3 px-4 bg-transparent border-none cursor-pointer text-[13px] font-medium transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              activeTab === tab
                ? 'text-text-primary border-b-2 border-b-brand'
                : 'text-text-muted border-b-2 border-b-transparent hover:text-text-secondary'
            )}
          >
            {tab === 'ideas'
              ? t('rightSidebar.savedIdeas', { count: ideas.length })
              : t('rightSidebar.history', { count: historyItems.length })}
          </button>
        ))}
      </div>

      {activeTab === 'ideas' ? (
        <>
          {/* Folders */}
          <div className="p-3 border-b border-border-subtle">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-[10px] uppercase tracking-[0.08em] text-text-faint m-0 font-semibold">
                {t('rightSidebar.folders')}
              </h3>
              <button
                onClick={() => setShowNewFolder(!showNewFolder)}
                className="bg-transparent border-none text-brand cursor-pointer text-[11px] font-medium hover:text-brand-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
              >
                {t('rightSidebar.newFolder')}
              </button>
            </div>

            {showNewFolder && (
              <div className="flex gap-1.5 mb-2 animate-fade-in">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddFolder()}
                  placeholder={t('rightSidebar.folderPlaceholder')}
                  autoFocus
                  className="flex-1 py-1.5 px-2.5 bg-surface-2 border border-border rounded-lg text-text-primary text-[12px] font-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 placeholder:text-text-faint"
                />
                <button
                  onClick={handleAddFolder}
                  className="py-1.5 px-3 bg-brand border-none rounded-lg text-white cursor-pointer text-[11px] font-semibold hover:bg-brand-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
                >
                  {t('common.add')}
                </button>
              </div>
            )}

            <div className="space-y-px">
              <button
                onClick={() => setSelectedFolder('all')}
                className={cn(
                  'w-full py-1.5 px-2.5 border-none rounded-lg cursor-pointer text-left flex justify-between items-center text-[12px]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 transition-colors duration-150',
                  selectedFolder === 'all'
                    ? 'bg-brand-muted text-brand font-semibold'
                    : 'bg-transparent text-text-secondary hover:bg-white/[0.04]'
                )}
              >
                <span>{t('rightSidebar.allIdeas')}</span>
                <span className="text-[10px] text-text-muted tabular-nums">{getFolderCount('all')}</span>
              </button>

              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.id)}
                  className={cn(
                    'w-full py-1.5 px-2.5 border-none rounded-lg cursor-pointer text-left flex justify-between items-center text-[12px]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 transition-colors duration-150',
                    selectedFolder === folder.id
                      ? 'bg-brand-muted text-brand font-semibold'
                      : 'bg-transparent text-text-secondary hover:bg-white/[0.04]'
                  )}
                >
                  <span>{folder.name}</span>
                  <span className="text-[10px] text-text-muted tabular-nums">{getFolderCount(folder.id)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Idea Cards */}
          <div className="p-3 flex-1 min-h-0 overflow-y-auto">
            {filteredIdeas.length === 0 ? (
              <div className="text-center py-8 px-3 text-text-muted text-[12px] whitespace-pre-wrap">
                {t('rightSidebar.emptyIdeas')}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredIdeas.map((idea) => (
                  <div
                    key={idea.id}
                    className="p-3 bg-surface-2 border border-border-subtle rounded-xl relative transition-all duration-200 hover:border-border-hover hover:bg-surface-3 group"
                  >
                    <div className="flex gap-1 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        onClick={() => navigator.clipboard.writeText(idea.text)}
                        className="bg-surface-3 border border-border text-text-muted cursor-pointer text-[10px] py-0.5 px-1.5 rounded hover:text-brand hover:border-brand/50 transition-colors focus-visible:outline-none"
                        title={t('common.copy')}
                      >
                        {t('common.copy')}
                      </button>
                      <button
                        onClick={() => handleDeleteIdea(idea.id)}
                        className="bg-surface-3 border border-border text-text-muted cursor-pointer text-[10px] py-0.5 px-1.5 rounded hover:text-danger hover:border-danger/50 transition-colors focus-visible:outline-none"
                        title={t('common.delete')}
                      >
                        &times;
                      </button>
                    </div>
                    <p className="text-[12px] leading-[1.55] mt-0 mb-1.5 text-text-primary pr-[40px]">
                      {idea.text}
                    </p>
                    <div className="text-[10px] text-text-faint">
                      {formatRelativeTime(idea.createdAt, locale)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="p-3 flex-1 min-h-0 overflow-y-auto">
          {historyItems.length === 0 ? (
            <div className="text-center py-8 px-3 text-text-muted text-[12px]">
              {t('rightSidebar.emptyHistory')}
            </div>
          ) : (
            <div className="space-y-2">
              {historyItems.slice(0, 30).map((item) => (
                <div
                  key={item.id}
                  className="p-2.5 bg-surface-2 border border-border-subtle rounded-xl cursor-pointer transition-all duration-200 hover:border-border-hover hover:bg-surface-3"
                  onClick={() => navigator.clipboard.writeText(item.content)}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={cn(
                        'py-0.5 px-1.5 rounded text-[9px] font-semibold uppercase tracking-wider',
                        typeStyles[item.type] || 'bg-surface-3 text-text-muted'
                      )}
                    >
                      {item.type}
                    </span>
                    <span className="text-[10px] text-text-faint">
                      {formatRelativeTime(item.timestamp, locale)}
                    </span>
                  </div>
                  <p className="text-[12px] leading-[1.45] m-0 text-text-secondary line-clamp-3">
                    {item.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
